import { useEffect, useRef } from 'react'

// Define the type for the effect function
type EffectCallback<U extends any[]> = (nonTriggerValues: [...U]) => void;

/**
 * Custom hook that wraps useEffect to only run when specified trigger dependencies change.
 *
 * @param {EffectCallback} effect - The effect function to run.
 * @param {T} triggerDeps - List of dependencies that trigger the effect when changed.
 * @param {U} nonTriggerDeps - List of dependencies whose values are used inside the effect but do not trigger it.
 */
function useConditionalEffect<T extends any[], U extends any[]>(
  effect: EffectCallback<U>,
  triggerDeps: [...T],
  nonTriggerDeps: [...U]
): void {
  // Refs to store the current values of all non-trigger dependencies
  const nonTriggerRefs = useRef(nonTriggerDeps)

  // Effect to update refs whenever non-trigger dependencies change
  useEffect(() => {
    nonTriggerRefs.current = [...nonTriggerDeps]
  }, [nonTriggerDeps])

  // Main effect that runs when trigger dependencies change
  useEffect(() => {
    // Execute the effect function with the latest values of non-trigger dependencies
    effect(nonTriggerRefs.current)
  }, [...triggerDeps]) // Only run this effect when trigger dependencies change
}

export default useConditionalEffect
